import { open } from '@tauri-apps/plugin-dialog';
import { readDir, readTextFile, writeTextFile, exists, BaseDirectory, mkdir } from '@tauri-apps/plugin-fs';
import { homeDir, appLocalDataDir } from '@tauri-apps/api/path';
import { Command } from '@tauri-apps/plugin-shell';
import { isWindows, getSeparator, joinPaths, sanitizeFilename } from './services/path-utils';

// Avoid circular dependency with runtime-bridge
const isTauri = () => {
  return typeof window !== 'undefined' && (
    '__TAURI__' in window ||
    '__TAURI_INTERNALS__' in window
  );
};

const SKETCHES_PATH_KEY = 'py2g_sketches_path';

export interface LocalSketch {
  id: string;
  title: string;
  description?: string;
  updatedAt: string;
  filePath: string;
  svgThumbnail?: string;
  code?: string; // Optional, might not load full code for list
}

export const desktopService = {
  async getSketchesPath(): Promise<string | null> {
    if (!isTauri()) return null;
    return localStorage.getItem(SKETCHES_PATH_KEY);
  },

  async selectSketchesFolder(): Promise<string | null> {
    if (!isTauri()) return null;

    try {
      const selected = await open({
        directory: true,
        multiple: false,
        title: 'Select Sketches Folder',
      });

      if (selected && typeof selected === 'string') {
        localStorage.setItem(SKETCHES_PATH_KEY, selected);
        return selected;
      }
    } catch (err) {
      console.error('Failed to select folder:', err);
    }
    return null;
  },

  async listSketches(): Promise<LocalSketch[]> {
    if (!isTauri()) return [];

    const path = await this.getSketchesPath();
    if (!path) return [];

    try {
      const entries = await readDir(path);
      const sketches: LocalSketch[] = [];

      for (const entry of entries) {
        if (entry.isFile && entry.name.endsWith('.json')) {
          try {
            // Read file to get metadata
            // We construct path manually or use join if available, but simple string concat works for now if we are careful
            // Actually, entry.name is just the name. We need full path.
            // Tauri's readTextFile takes a path.
            // Let's assume standard path separators.
            const filePath = joinPaths(path, entry.name);

            const content = await readTextFile(filePath);
            const data = JSON.parse(content);

            // Validate it's a sketch
            if (data.id && data.title) {
              sketches.push({
                id: data.id,
                title: data.title,
                description: data.description,
                updatedAt: data.updatedAt || new Date().toISOString(),
                filePath: filePath,
                svgThumbnail: data.svgThumbnail
              });
            }
          } catch (e) {
            console.warn(`Failed to parse sketch ${entry.name}:`, e);
          }
        }
      }

      // Sort by updated recently
      return sketches.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } catch (err) {
      console.error('Failed to list sketches:', err);
      return [];
    }
  },

  async saveSketch(sketch: any): Promise<boolean> {
    if (!isTauri()) return false;

    const path = await this.getSketchesPath();
    if (!path) {
      throw new Error("No sketches folder selected");
    }

    try {
      // Sanitize filename
      const filename = `${sanitizeFilename(sketch.title)}_${sketch.id}.json`;
      const filePath = joinPaths(path, filename);

      const content = JSON.stringify({
        ...sketch,
        updatedAt: new Date().toISOString()
      }, null, 2);

      await writeTextFile(filePath, content);
      return true;
    } catch (err) {
      console.error('Failed to save sketch:', err);
      throw err;
    }
  },

  async loadSketch(id: string): Promise<any | null> {
    // This is tricky because we only have ID. 
    // We need to find the file with this ID.
    // For now, let's assume we list all and find it.
    const sketches = await this.listSketches();
    const match = sketches.find(s => s.id === id);
    if (match) {
      const content = await readTextFile(match.filePath);
      return JSON.parse(content);
    }
    return null;
  },

  // Python Environment Management

  async getSitePackagesPath(): Promise<string> {
    const dataDir = await appLocalDataDir();
    return joinPaths(dataDir, 'site-packages');
  },

  async getVenvPath(sketchId: string): Promise<string> {
    const dataDir = await appLocalDataDir();
    return joinPaths(dataDir, 'venvs', sketchId);
  },

  async ensureVenv(sketchId: string): Promise<string> {
    const venvPath = await this.getVenvPath(sketchId);
    const info = await this.getPythonInfo();

    // Check if venv exists (simple check for pyvenv.cfg)
    const cfgPath = joinPaths(venvPath, 'pyvenv.cfg');

    try {
      const isWin = isWindows();
      const exists = await Command.create(isWin ? 'cmd' : 'test', isWin ? ['/C', `if exist "${cfgPath}" echo yes`] : ['-f', cfgPath]).execute();
      if (exists.code === 0 && exists.stdout.trim() === 'yes') {
        return venvPath;
      }
    } catch (e) {
      // ignore
    }

    // Create venv
    try {
      let command: Command<string>;
      if (info.type === 'uv') {
        const args = ['venv', venvPath];
        if (info.command[0].startsWith('sidecar:')) {
          const sidecarName = info.command[0].split(':')[1];
          command = Command.sidecar(sidecarName, args);
        } else {
          command = Command.create('uv', args);
        }
      } else {
        const args = ['-m', 'venv', venvPath];
        command = Command.create('python', args);
      }

      await command.execute();
      return venvPath;
    } catch (e) {
      console.error('Failed to create venv:', e);
      throw e;
    }
  },

  async installPackageInVenv(sketchId: string, packageName: string): Promise<{ success: boolean; output: string }> {
    if (!isTauri()) return { success: false, output: 'Not in desktop mode' };

    try {
      const venvPath = await this.ensureVenv(sketchId);
      const info = await this.getPythonInfo();

      let command: Command<string>;

      if (info.type === 'uv') {
        // uv pip install -p <venv> <package>
        const args = ['pip', 'install', '-p', venvPath, packageName];
        if (info.command[0].startsWith('sidecar:')) {
          const sidecarName = info.command[0].split(':')[1];
          command = Command.sidecar(sidecarName, args);
        } else {
          command = Command.create('uv', args);
        }
      } else {
        // python -m pip install --prefix <venv> <package> ? No, activate or use python in venv
        // On windows: venv/Scripts/python -m pip install ...
        const isWin = isWindows();
        const pythonPath = joinPaths(venvPath, isWin ? 'Scripts' : 'bin', 'python');

        const args = ['-m', 'pip', 'install', packageName];
        command = Command.create(pythonPath, args);
      }

      const output = await command.execute();

      if (output.code === 0) {
        return { success: true, output: output.stdout };
      } else {
        return { success: false, output: output.stderr };
      }

    } catch (err: any) {
      return { success: false, output: err.message || String(err) };
    }
  },

  async getTempPath(): Promise<string> {
    const dataDir = await appLocalDataDir();
    const tempPath = joinPaths(dataDir, 'temp');
    const isWin = isWindows();

    try {
      // Try fs.mkdir first
      await mkdir(tempPath, { recursive: true });
    } catch (e) {
      console.warn('fs.mkdir failed, trying shell fallback', e);
      try {
        if (isWin) {
          // Use quotes to handle spaces
          await Command.create('cmd', ['/C', `if not exist "${tempPath}" mkdir "${tempPath}"`]).execute();
        } else {
          await Command.create('mkdir', ['-p', tempPath]).execute();
        }
      } catch (e2) {
        console.error('Failed to create temp dir', e2);
      }
    }

    return tempPath;
  },

  async getPythonInfo(): Promise<{ command: string[], type: 'uv' | 'system' }> {
    if (!isTauri()) return { command: ['python'], type: 'system' };

    try {
      // Try to use bundled uv sidecar first
      try {
        const uvSidecar = Command.sidecar('bin/uv', ['--version']);
        const uvResult = await uvSidecar.execute();
        if (uvResult.code === 0) {
          // If sidecar works, we use it.
          // Note: Command.sidecar returns a command that executes the sidecar binary.
          // But we need to return a command string array for other functions to use.
          // This is tricky because 'uv' in the array implies looking up in PATH or sidecar map.
          // For sidecars, we must use Command.sidecar('bin/uv', args).
          // So we should probably change the return type or how we use it.
          // For now, let's return a marker that we have a sidecar.
          return { command: ['sidecar:bin/uv', 'run', 'python'], type: 'uv' };
        }
      } catch (e) {
        console.warn('Bundled uv sidecar check failed:', e);
      }

      // Fallback to system uv
      try {
        const uvCheck = Command.create('uv', ['--version']);
        const uvResult = await uvCheck.execute();
        if (uvResult.code === 0) {
          return { command: ['uv', 'run', 'python'], type: 'uv' };
        }
      } catch (e) {
        // ignore
      }

      return { command: ['python'], type: 'system' };
    } catch (err) {
      console.error('Failed to get python info:', err);
      return { command: ['python'], type: 'system' };
    }
  },

  async installPackage(packageName: string): Promise<{ success: boolean; output: string }> {
    if (!isTauri()) return { success: false, output: 'Not in desktop mode' };

    try {
      const sitePackages = await this.getSitePackagesPath();
      const info = await this.getPythonInfo();

      let command: Command<string>;

      if (info.type === 'uv') {
        const args = ['pip', 'install', '--target', sitePackages, packageName];
        if (info.command[0].startsWith('sidecar:')) {
          const sidecarName = info.command[0].split(':')[1];
          command = Command.sidecar(sidecarName, args);
        } else {
          command = Command.create('uv', args);
        }
      } else {
        const args = ['-m', 'pip', 'install', '--target', sitePackages, packageName];
        command = Command.create('python', args);
      }

      const output = await command.execute();

      if (output.code === 0) {
        return { success: true, output: output.stdout };
      } else {
        return { success: false, output: output.stderr };
      }

    } catch (err: any) {
      return { success: false, output: err.message || String(err) };
    }
  },

  async listPackages(): Promise<{ name: string; version: string }[]> {
    if (!isTauri()) return [];

    try {
      const sitePackages = await this.getSitePackagesPath();
      const info = await this.getPythonInfo();

      let command: Command<string>;

      if (info.type === 'uv') {
        // For listing, we want to use python to list what's in the target dir
        // But if we are using uv, we might want to use `uv pip list`?
        // `uv pip list --target` is not supported directly?
        // Let's stick to `python -m pip list` via `uv run python`

        const baseArgs = info.command.slice(1); // ['run', 'python']
        const args = [...baseArgs, '-m', 'pip', 'list', '--path', sitePackages, '--format=json'];

        if (info.command[0].startsWith('sidecar:')) {
          const sidecarName = info.command[0].split(':')[1];
          command = Command.sidecar(sidecarName, args);
        } else {
          command = Command.create('uv', args);
        }
      } else {
        const args = ['-m', 'pip', 'list', '--path', sitePackages, '--format=json'];
        command = Command.create('python', args);
      }

      const output = await command.execute();

      if (output.code === 0) {
        try {
          return JSON.parse(output.stdout);
        } catch {
          return [];
        }
      }
      return [];
    } catch (err) {
      console.error('Failed to list packages:', err);
      return [];
    }
  }
};
